import { ITextVariablesClient, PollingStatuses, TextVariableDto, TextVariablesClient, TranslateInitiateStatuses } from "app/ApiClient";
import { initialTranslate, TranslateInitiateTokens, TranslateVariable, TranslationDownloadToken } from "./translateState";
import { AnyAction, createAction, createAsyncThunk, createSlice, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";
import { IState } from "slices";
import saveAs from "file-saver";
import { LoadingState } from "@material-dev/insights-portal-ui-components/lib/types";
import { getTextVariableStatByProjectId } from "slices/textVariablesProject/textVariablesSlice";


const textVariablesClient: ITextVariablesClient = new TextVariablesClient(process.env.REACT_APP_API_URL);
const TRANSLATIONS = "Translations";
const POLLING_INTERVAL_MS = 5000;
const TRANSLATION_DOWNLOAD_ERROR_MSG =
  "This file cannot be downloaded because translations for this dataset are not yet available.";

const addTranslateInitialToken = (
  token: string,
  projectId?: number,
  variableName?: string,
  status?: PollingStatuses,
) => {
  let translateInitiateTokens: TranslateInitiateTokens = {};
  translateInitiateTokens.projectId = projectId;
  translateInitiateTokens = setTranslateVariable(
    translateInitiateTokens,
    token,
    variableName,
    status,
  );
  return translateInitiateTokens;
};

const setTranslateVariable = (
  translateInitiateTokens: TranslateInitiateTokens,
  token: string,
  variableName?: string,
  status?: PollingStatuses,
) => {
  const translateVariable: TranslateVariable = {
    token: token,
    variableName: variableName,
    status: status,
  };

  const translateTokens = { ...translateInitiateTokens };
  if (translateTokens.variables == null) {
    translateTokens.variables = [];
  }
  if (translateVariable) {
    translateTokens.variables.push(translateVariable);
  }
  return translateTokens;
};

const deleteTranslateToken = (
  translateInitiateTokens: TranslateInitiateTokens[],
  token?: string,
  variableName?: string,
  projectId?: number,
) => {
  let translateTokenList = [];
  if (token) {
    translateTokenList = translateInitiateTokens.map((item) => ({
      ...item,
      variables: (item.variables ?? []).filter((v) => v.token !== token),
    }));
  } else {
    translateTokenList = translateInitiateTokens.map((item) => {
      if (item.projectId === projectId) {
        return {
          ...item,
          variables: (item.variables ?? []).filter((v) => v.variableName !== variableName),
        };
      }
      return item;
    });
  }

  return translateTokenList.filter((x) => x.variables?.length !== 0);
};

const updateTranslateToken = (
  translateInitiateTokens: TranslateInitiateTokens[],
  token: string,
  projectId?: number,
  variableName?: string,
  status?: PollingStatuses,
) => {
  let translateInitiateToken = translateInitiateTokens.find((x) => x.projectId == projectId);
  if (translateInitiateToken) {
    if (translateInitiateToken) {
      const variable = translateInitiateToken.variables?.find((x) => x.variableName == variableName);
      if (variable) {
        variable.token = token;
        variable.status = status;
      } else {
        translateInitiateToken = setTranslateVariable(
          translateInitiateToken,
          token,
          variableName,
          status,
        );
      }
    }
  } else {
    translateInitiateTokens.push(addTranslateInitialToken(token, projectId, variableName, status));
  }
  return translateInitiateTokens;
};

export const initiateVariableTranslation = createAsyncThunk(
    "textVariables/initiateVariableTranslation",
    async (
      { projectId, languageVariableName, variables, batteryOrVariableFullname, encodedVersionId }: { projectId: number; languageVariableName: string | undefined; variables: TextVariableDto[], batteryOrVariableFullname: string | undefined, encodedVersionId: string },
      { dispatch },
    ) => {
      const result: Array<{ variable: TextVariableDto; isSuccess: boolean; }> = [];     
      const taskList: Array<Promise<unknown>> = [];
      for (const variable of variables) {
        const { variableName = "" } = variable;
        let isSuccess = false;
        try {
          const apiResult = await textVariablesClient.initiateTranslation({
            languageVariableName,
            projectId,
            variableName,
          });
          if (apiResult) {
            if (apiResult.token) {
              dispatch(
                translateInitiateTokenReceived({
                  token: apiResult.token,
                  projectId,
                  variableName,
                  status: PollingStatuses.InProgress,
                }),
              );
             
              // Creating task for each polling request to start at once to keep track of all variables successfully completed translation process.
              const pollingTask = new Promise((resolve, reject) => 
                dispatch(getTranslateInitiateStatus({ token: apiResult.token ?? "", variableName: variableName, encodedVersionId: encodedVersionId, resolve, reject }))
              );
              taskList.push(pollingTask);
            }
            isSuccess = true;
          }
        } catch (err) {
          //TODO: may log error, or return it
        }
        result.push({ variable, isSuccess });
      }
      Promise.all(taskList).then((results) => {
        dispatch(updateResultMessageTranslateVariable({projectId:projectId, name:batteryOrVariableFullname}));
        return results;
      }).catch((error)=>{
        dispatch(updateErrorMessageTranslateVariable({projectId:projectId, name:batteryOrVariableFullname}));
        throw error;
      });
  
      return result;      
    },
  );
  
export const getTranslateInitiateStatus = createAsyncThunk(
    "textVariables/getTranslateInitiateStatus",
    async ({ token, variableName, encodedVersionId, resolve, reject }: { token: string; variableName: string, encodedVersionId: string, resolve:(value:string)=>void, reject:()=>void }, { dispatch, getState }) => {
      const {
        translate: { translateInitiateTokens },
      } = getState() as IState;
      const translateToken = translateInitiateTokens?.find((x) =>
        x.variables?.find((v) => v.token == token),
      );
      const projectId = translateToken && translateToken.projectId;
      try {
        if (!projectId) return;
  
        const result = await textVariablesClient.getTranslateInitiateStatus(projectId ?? 0,
                             token ?? "");
        switch (result?.status) {
          case TranslateInitiateStatuses.Completed:
            await dispatch(getTextVariableStatByProjectId({ projectId: projectId, versionId: encodedVersionId??"" }));
            dispatch(
              translateInitiateTokenRemoved({
                token: token,
                projectId,
                variableName,
              }),              
            );
            resolve('completed');
            break;
          case TranslateInitiateStatuses.InProgress:
            await dispatch(getTextVariableStatByProjectId({ projectId: projectId, versionId: encodedVersionId??"" }));
            setTimeout(() => {
              dispatch(
                translateInitiateTokenReceived({
                  token: token,
                  projectId,
                  variableName,
                  status: PollingStatuses.InProgress,
                }),
              );              
              dispatch(getTranslateInitiateStatus({ token, variableName, encodedVersionId,  resolve, reject  }));
            }, POLLING_INTERVAL_MS);
            break;
          default:
            dispatch(
              translateInitiateTokenRemoved({
                token: token,
                projectId,
                variableName,
              }),              
            );
            reject();
            break;
        }
      } catch (error) {
        dispatch(
          translateInitiateTokenRemoved({
            token: token,
            projectId,
            variableName,
          }),
        );
        reject();
      }
    },
  ); 
 
  const setTranslationDownloadToken = (
    translationDownloadTokens: TranslationDownloadToken[],
    token: string,
    projectId: number,
  ) => {
    let translationDownloadToken: TranslationDownloadToken | undefined = translationDownloadTokens.find(
      (x) => x.projectId == projectId,
    );
    if (translationDownloadToken) {
      translationDownloadToken.token = token;
    } else {
      translationDownloadToken = {
        token: token,
        projectId: projectId,
      };
  
      translationDownloadTokens.push(translationDownloadToken);
    }
    return translationDownloadTokens;
  };
  
  const deleteTranslationDownloadToken = (
    translationDownloadTokens: TranslationDownloadToken[],
    token?: string,
    projectId?: number,
  ) => {
    if (token) {
      return translationDownloadTokens.filter((x) => x.token !== token);
    } else {
      return translationDownloadTokens.filter((x) => x.projectId !== projectId);
    }
  };
  
  const setTranslationDownloadTokenAndGetTokenStatus = (
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
    token: string,
    projectId: number,
  ) => {
    setTimeout(() => {
      dispatch(
        downloadTranslationTokenReceived({
          token: token,
          projectId,
        }),
      );
  
      dispatch(getTranslationDownloadStatus({ token }));
    }, POLLING_INTERVAL_MS);
  };
  
  const deleteTranslationDownloadTokenOnError = (
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
    token: string,
    projectId?: number,
    currentProjectId?: number,
  ) => {
    dispatch(
      downloadTranslationTokenRemoved({
        token: token,
        status: LoadingState.failed,
        projectId: projectId,
        currentProjectId: currentProjectId,
      }),
    );
  };
  
  export const downloadTranslations = createAsyncThunk(
    "textVariables/downloadTranslations",
    async (projectId: number, { dispatch, rejectWithValue, getState }) => {
      const {
        project: { currentProject },
      } = getState() as IState;
      try {
        const result = await textVariablesClient.downloadTranslation(projectId);
        if (result) {
          if (result.token) {
            setTranslationDownloadTokenAndGetTokenStatus(dispatch, result.token, projectId);
          }
        }
      } catch (error) {
        deleteTranslationDownloadTokenOnError(dispatch, "", projectId, currentProject?.id);
        return rejectWithValue(error);
      }
    },
  );
  
  export const getTranslationDownloadStatus = createAsyncThunk(
    "textVariables/getTranslationDownloadStatus",
    async ({ token }: { token: string }, { dispatch, getState }) => {
      const {
        translate: { translationDownloadTokens },
      } = getState() as IState;
      const {
        project: { currentProject },
      } = getState() as IState;
      const projectId: number | undefined = translationDownloadTokens?.find((x) => x.token == token)?.projectId;
      try {
        if (!projectId) return;
        const result = await textVariablesClient.getTranslationDownloadStatus(projectId ?? 0, token ?? "");
        switch (result?.status) {
          case PollingStatuses.Complete:
            dispatch(
              downloadTranslationTokenRemoved({
                token: token,
                status: LoadingState.succeed,
                projectId: projectId,
                currentProjectId: currentProject?.id,
              }),
            );
            if (result.presignedUrl) {
              dispatch(setPresignedUrl(result.presignedUrl));
              saveAs(result.presignedUrl);
            }
            break;
          case PollingStatuses.InProgress:
            setTranslationDownloadTokenAndGetTokenStatus(dispatch, token, projectId);
            break;
          default:
            dispatch(
              downloadTranslationTokenRemoved({
                token,
                status: LoadingState.failed,
                projectId: projectId,
                errorMessage: result.message,
                currentProjectId: currentProject?.id,
              }),
            );
            break;
        }
      } catch (error) {
        deleteTranslationDownloadTokenOnError(dispatch, token, projectId, currentProject?.id);
      }
    },
  );
  
export const resetInitTransaltionStatus = createAction<number>("textVariables/downloadOpenEndCsv");

const translateSlice = createSlice({
    name: "textVariables",
    initialState: initialTranslate,
    reducers:  {
        resetTranslateStatus: (state) => {
          state.initiateTranslateStatus = LoadingState.idle;
          state.errorMessage = undefined;  
          state.translationDownloadingState = LoadingState.idle;
          state.downloadTranslationErrorMessage = undefined;
        },       
        clearResultForTranslateVariable:(state, { payload }:{ payload: { name?:string, projectId?:number}}) => {
          const { projectId, name} = payload;
          state.translateResultMessageVariable = [
            ...state.translateResultMessageVariable.filter(result=>!(result.projectId===projectId && result.name===name))
          ];
        },
        clearErrorForTranslateVariable:(state, { payload }:{ payload: { name?:string, projectId?:number}}) => {
          const { projectId, name} = payload;
          state.translateErrorMessageVariable = [
            ...state.translateErrorMessageVariable.filter((result=>result.projectId===projectId && result.name===name))
          ];
        },
        updateResultMessageTranslateVariable:(state, {payload}:{payload:{name?:string, projectId:number}}) => {
          state.translateResultMessageVariable.push({projectId:payload.projectId, name:payload.name});
        },
        updateErrorMessageTranslateVariable:(state, {payload}:{payload:{name?:string, projectId:number}}) => {
          state.translateErrorMessageVariable.push({projectId:payload.projectId, name:payload.name});
        },
        translateInitiateTokenReceived: (
          state,
          {
            payload: { token, projectId, variableName, status },
          }: { payload: { token: string; projectId?: number; variableName: string; status: PollingStatuses } },
        ) => {
          if (state.translateInitiateTokens == null) {
            state.translateInitiateTokens = [];
          }
          state.translateInitiateTokens = updateTranslateToken(
            state.translateInitiateTokens,
            token,
            projectId,
            variableName,
            status,
          );
        },
        translateInitiateTokenRemoved: (
          state,
          {
            payload: { token, projectId, variableName },
          }: { payload: { token: string; projectId?: number; variableName: string } },
        ) => {
          if (state.translateInitiateTokens && state.translateInitiateTokens.length > 0) {
              state.translateInitiateTokens = deleteTranslateToken(
              state.translateInitiateTokens,
              token,
              variableName,
              projectId,
            );
          }
        },
        downloadTranslationTokenReceived: (
            state,
            { payload: { token, projectId } }: { payload: { token: string; projectId: number } },
          ) => {
            state.translationDownloadTokens = setTranslationDownloadToken(state.translationDownloadTokens, token, projectId);
        },
        downloadTranslationTokenRemoved: (
            state,
            {
              payload: { token, status, projectId, currentProjectId, errorMessage },
            }: {
              payload: {
                token: string;
                status: LoadingState;
                projectId?: number;
                currentProjectId?: number;
                errorMessage?: string;
              };
            },
          ) => {
            state.translationDownloadTokens = deleteTranslationDownloadToken(
              state.translationDownloadTokens,
              token,
              projectId,
            );
            if (currentProjectId && projectId === currentProjectId) {
              state.translationDownloadingState = status;
              if (status == LoadingState.failed) {
                state.downloadTranslationErrorMessage = errorMessage
                  ? `${TRANSLATIONS} ${errorMessage}`
                  : TRANSLATION_DOWNLOAD_ERROR_MSG;
              } else {
                state.downloadTranslationErrorMessage = undefined;
              }
            } else {
              state.downloadTranslationErrorMessage = undefined;
            }
        }, 
        setPresignedUrl(state, action: PayloadAction<string | null>) {
          state.presignedUrl = action.payload;
        },
      },
    extraReducers: (builder) => {
      builder.addCase(downloadTranslations.pending, (state, { meta: { arg } }) => {
        state.translationDownloadingState = LoadingState.pending;
        state.downloadTranslationErrorMessage = undefined;
        state.translationDownloadTokens = setTranslationDownloadToken(state.translationDownloadTokens, "", arg);
      });

      builder.addCase(
        initiateVariableTranslation.pending,
        (
          state,
          {
            meta: {
              arg: { variables, projectId },
            },
          },
        ) => {
          state.initiateTranslateStatus = LoadingState.pending;
          state.errorMessage = undefined;
          if (state.translateInitiateTokens == null) {
            state.translateInitiateTokens = [];
          }
          for (const variable of variables) {
            state.translateInitiateTokens = updateTranslateToken(
              state.translateInitiateTokens,
              "",
              projectId,
              variable.variableName,
              PollingStatuses.InProgress,
            );
          }
        },
      );
      
      builder.addCase(initiateVariableTranslation.fulfilled, (state, { payload }) => {
        state.initiateTranslateStatus = payload.every((x) => x.isSuccess)
          ? LoadingState.succeed
          : LoadingState.failed;
        state.translateVariables= payload.map((x) => x.variable);
        state.errorMessage = undefined;
      });
  
      builder.addCase(initiateVariableTranslation.rejected,  (
        state,
        {
          meta: {
            arg: { variables, projectId },
          },
          error,
        },
      ) => {
        state.initiateTranslateStatus = LoadingState.failed;
        state.translateVariables= variables;
        state.errorMessage = error.message;
        for (const variable of variables) {
          if (state.translateInitiateTokens && state.translateInitiateTokens.length > 0) {
            state.translateInitiateTokens = deleteTranslateToken(
              state.translateInitiateTokens,
              "",
              variable.variableName,
              projectId,
            );
          }
        }
      });

      builder.addCase(resetInitTransaltionStatus, (state) => {
        state.initiateTranslateStatus = LoadingState.idle;
        state.errorMessage = undefined;
        state.translationDownloadingState = LoadingState.idle;
      }); 
    },
  });
  export const { 
  resetTranslateStatus,
  translateInitiateTokenReceived,
  translateInitiateTokenRemoved,
  downloadTranslationTokenReceived, 
  downloadTranslationTokenRemoved,
  clearResultForTranslateVariable,
  clearErrorForTranslateVariable,
  updateResultMessageTranslateVariable,
  updateErrorMessageTranslateVariable,
  setPresignedUrl
   } = translateSlice.actions;
  export default translateSlice.reducer;
